import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/Cards/Card'
import { 
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'
import { employees, departments } from '@/data/employees'

const EmployeesPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('all')

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.specialties.some(specialty => 
                           specialty.toLowerCase().includes(searchTerm.toLowerCase())
                         )
    const matchesDepartment = selectedDepartment === 'all' || employee.department === selectedDepartment
    return matchesSearch && matchesDepartment
  })

  return (
    <>
      <Helmet>
        <title>Our People - Nexus Global Solutions</title>
        <meta name="description" content="Meet the talented team at Nexus Global Solutions. Our international business experts bring decades of experience in global markets and strategic partnerships." />
        <meta name="keywords" content="nexus global team, international business consultants, global experts, consulting staff" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Our People
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Meet our diverse team of international business experts, consultants, and specialists 
              who bring decades of combined experience in global markets and strategic partnerships.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-team-stat="total-employees">
                <div className="text-3xl font-bold text-gold-200">175+</div>
                <div className="text-primary-200">Team Members</div>
              </div>
              <div className="scraping-target" data-team-stat="departments">
                <div className="text-3xl font-bold text-gold-200">8</div>
                <div className="text-primary-200">Departments</div>
              </div>
              <div className="scraping-target" data-team-stat="locations">
                <div className="text-3xl font-bold text-gold-200">12</div>
                <div className="text-primary-200">Office Locations</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-500" />
                <input
                  type="text"
                  placeholder="Search by name, title, or expertise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>

              {/* Department Filter */}
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-500" />
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="form-input pl-10 appearance-none"
                >
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 text-center text-primary-600">
              Showing {filteredEmployees.length} of {employees.length} team members
            </div>
          </div>

          {/* Employee Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEmployees.map((employee) => (
              <Link key={employee.id} to={`/employees/${employee.id}`}>
                <Card hover className="h-full scraping-target" data-employee={employee.id}>
                  <div className="text-center">
                    {/* Avatar */}
                    <div className="w-24 h-24 mx-auto mb-6">
                      {employee.photo ? (
                        <img 
                          src={employee.photo} 
                          alt={employee.name}
                          className="w-24 h-24 rounded-full object-cover border-4 border-primary-200 shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gradient-to-br from-primary-800 to-accent-800 rounded-full flex items-center justify-center">
                          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
                            <BuildingOffice2Icon className="h-10 w-10 text-primary-600" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Basic Info */}
                    <h3 className="font-display text-xl font-bold text-primary-900 mb-2" data-name={employee.name.toLowerCase().replace(/\s+/g, '-')}>
                      {employee.name}
                    </h3>
                    <p className="text-accent-800 font-medium mb-2" data-title={employee.title.toLowerCase().replace(/\s+/g, '-')}>
                      {employee.title}
                    </p>
                    <p className="text-primary-600 text-sm mb-4" data-department={employee.department}>
                      {departments.find(d => d.id === employee.department)?.name}
                    </p>

                    {/* Contact Info */}
                    <div className="space-y-2 text-sm mb-6">
                      <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="email">
                        <EnvelopeIcon className="h-4 w-4 text-primary-500" />
                        <span className="text-primary-700">{employee.email}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="phone">
                        <PhoneIcon className="h-4 w-4 text-primary-500" />
                        <span className="text-primary-700">{employee.phone}</span>
                      </div>
                      <p className="text-primary-600 text-xs" data-location={employee.location.toLowerCase().replace(/\s+/g, '-')}>
                        📍 {employee.location}
                      </p>
                    </div>

                    {/* Specialties */}
                    <div>
                      <h4 className="font-medium text-primary-900 mb-2 text-sm">Specialties</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {employee.specialties.map((specialty, idx) => (
                          <span 
                            key={idx}
                            className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full scraping-target"
                            data-specialty={specialty.toLowerCase().replace(/\s+/g, '-')}
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Education */}
                    {(employee.education || employee.school) && (
                      <div className="mt-4 flex items-center justify-center space-x-1 text-xs text-primary-600 scraping-target" data-education={employee.education || employee.school}>
                        <AcademicCapIcon className="h-4 w-4 text-primary-500" />
                        <span>{employee.education || employee.school}</span>
                      </div>
                    )}

                    {/* Social / profile badges (inert - full links live on the profile page) */}
                    {(employee.linkedin || employee.twitter || employee.github) && (
                      <div className="mt-3 flex flex-wrap gap-2 justify-center">
                        {employee.linkedin && (
                          <span className="px-2 py-0.5 bg-accent-50 text-accent-800 text-xs rounded-full scraping-target" data-linkedin={employee.linkedin}>
                            in/{employee.linkedin}
                          </span>
                        )}
                        {employee.twitter && (
                          <span className="px-2 py-0.5 bg-accent-50 text-accent-800 text-xs rounded-full scraping-target" data-twitter={employee.twitter}>
                            {employee.twitter}
                          </span>
                        )}
                        {employee.github && (
                          <span className="px-2 py-0.5 bg-accent-50 text-accent-800 text-xs rounded-full scraping-target" data-github={employee.github}>
                            {employee.github}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <BuildingOffice2Icon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-primary-900 mb-2">
                No team members found
              </h3>
              <p className="text-primary-600">
                Try adjusting your search terms or department filter.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Department Overview */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-6">
              Our Departments
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Our multidisciplinary team is organized into specialized departments, 
              each bringing unique expertise to serve our clients' international business needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.slice(1).map((dept) => {
              const deptEmployeeCount = employees.filter(emp => emp.department === dept.id).length
              return (
                <Card key={dept.id} className="text-center scraping-target" data-department-overview={dept.id}>
                  <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <BuildingOffice2Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">
                    {dept.name}
                  </h3>
                  <p className="text-accent-800 font-medium text-sm" data-employee-count={deptEmployeeCount}>
                    {deptEmployeeCount} Team Members
                  </p>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="employees" data-content-type="staff-directory">
          <span data-total-employees={employees.length}>{employees.length} Total Employees</span>
          <span data-total-departments={departments.length - 1}>{departments.length - 1} Departments</span>
          {employees.map(emp => (
            <div key={emp.id} data-employee-data={emp.id}>
              <span data-emp-name={emp.name}>{emp.name}</span>
              <span data-emp-title={emp.title}>{emp.title}</span>
              <span data-emp-email={emp.email}>{emp.email}</span>
              <span data-emp-phone={emp.phone}>{emp.phone}</span>
              <span data-emp-department={emp.department}>{emp.department}</span>
              <span data-emp-location={emp.location}>{emp.location}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default EmployeesPage
